import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, ConfirmDialog, Input, Spinner, Toggle } from "../../../components/ui";
import "./live-lookbook-link.scss";

// Live store lookbook — one permanent link per store that shows the lookbook
// grid of the store's ACTIVE listings, read on every open (sold watches drop
// off by themselves), with a Download PDF button for the grid pages only.
// GET/PUT /api/lookbooks/live-link; the token is created on first load.
// Rendered inside the "Live Lookbook" dialog on Listings (RCListingsPage),
// which supplies the title.
export function LiveLookbookLink() {
  const [link, setLink] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    axios
      .get("/api/lookbooks/live-link")
      .then((res) => setLink(res.data))
      .catch((e) => setError(e?.response?.data?.message || "Could not load the live lookbook link."));
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  // Toggle rolls back on a rejected promise, so let the error propagate.
  const saveSetting = (key) => async (value) => {
    const res = await axios.put("/api/lookbooks/live-link", { [key]: value });
    setLink(res.data);
  };

  const regenerate = async () => {
    setRegenerating(true);
    setError("");
    try {
      const res = await axios.put("/api/lookbooks/live-link", { regenerate: true });
      setLink(res.data);
      setConfirmOpen(false);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not generate a new link.");
    } finally {
      setRegenerating(false);
    }
  };

  if (!link) {
    return error
      ? <p className="rc-live-lookbook__error">{error}</p>
      : <div className="rc-live-lookbook__loading"><Spinner /></div>;
  }

  return (
    <div className="rc-live-lookbook" data-testid="live-lookbook-link">
      <div className="rc-live-lookbook__row">
        <Input size="md" readOnly value={link.url} onFocus={(e) => e.target.select()} aria-label="Live lookbook link" fullWidth />
        <Button variant="primary" onClick={copy}>{copied ? "Copied ✓" : "Copy"}</Button>
      </div>

      <div className="rc-live-lookbook__toggles">
        <Toggle size="sm" label="Hide prices" checked={link.hide_prices} onChange={saveSetting("hide_prices")} />
        {/* Each watch links to its product page; on = plain tiles. */}
        <Toggle size="sm" label="Hide listing links" checked={!!link.hide_links} onChange={saveSetting("hide_links")} />
      </div>

      <div className="rc-live-lookbook__actions">
        <Button variant="primary" href={link.url} target="_blank" rel="noreferrer noopener">Open</Button>
        {/* Same grid-only PDF as the live page's own Download PDF button. */}
        <Button variant="secondary" href={`${link.url}/pdf`} data-btn="live-lookbook-pdf">Download PDF</Button>
        <Button variant="ghost" onClick={() => setConfirmOpen(true)}>New link</Button>
      </div>

      {error && <p className="rc-live-lookbook__error">{error}</p>}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={regenerate}
        loading={regenerating}
        danger
        title="Generate a new link?"
        description="The current link stops working immediately. Anyone you sent it to will need the new one."
        confirmLabel="Generate new link"
      />
    </div>
  );
}

export default LiveLookbookLink;
